import React, { FunctionComponent, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { find, isNil } from 'lodash'
import {
  DataGrid,
  GridColDef,
  GridColTypeDef,
  GridFeatureMode,
  GridFilterModelParams,
  GridPageChangeParams,
  GridRowsProp,
  GridSelectionModelChangeParams,
  GridSortItem,
  GridSortModelParams
} from '@material-ui/data-grid'
import { Expression, List, Operator, Schema } from '../api/FunctionApp'
import { camelCaseToTitle } from '../api/Utils'

const convertOperator = (operator: string): Operator => {
  switch (operator) {
    case '=':
    case 'equals':
    case 'is':
      return Operator.Equal

    case '!=':
    case 'not':
      return Operator.NotEqual

    case '>':
    case 'after':
      return Operator.GreaterThan

    case '>=':
    case 'onOrAfter':
      return Operator.GreaterThanEqual

    case '<':
    case 'before':
      return Operator.LessThan

    case '<=':
    case 'onOrBefore':
      return Operator.LessThanEqual

    case 'contains': return Operator.Contains
    case 'endsWith': return Operator.EndsWith
    case 'startsWith': return Operator.StartsWith

    default: throw Error(`Operator string not supported! ${operator}`)
  }
}

interface Props {
  checkboxSelection?: boolean,
  customColumns?: { property: string, gridColTypeDef: GridColTypeDef }[],
  data?: List,
  filterMode?: GridFeatureMode,
  idProperty?: string,
  ignoredProperties?: string[],
  loading?: boolean,
  onFilterChange?: (expressions: Expression[]) => void,
  onPageChange?: (page: number) => void,
  onRowsPerPageChange?: (rowsPerPage: number) => void,
  onSelectionChange?: (ids: number[]) => void,
  onSortChange?: (params: GridSortItem[]) => void,
  page?: number,
  paginationMode?: GridFeatureMode
  rowsPerPage?: number,
  rowsPerPageOptions?: number[],
  schema: Schema,
  sort?: GridSortItem[],
  sortingMode?: GridFeatureMode,
  totalRows?: number,
  widths?: Record<string, number>
}

const EnhancedDataGrid: FunctionComponent<Props> = (props: Props) => {
  const {
    checkboxSelection,
    customColumns,
    data,
    filterMode,
    idProperty,
    ignoredProperties,
    loading,
    onFilterChange,
    onPageChange,
    onRowsPerPageChange,
    onSelectionChange,
    onSortChange,
    page,
    paginationMode,
    rowsPerPage,
    rowsPerPageOptions,
    schema,
    sort,
    sortingMode,
    totalRows,
    widths
  } = props
  const intl = useIntl()

  const rows: GridRowsProp = useMemo(() => {
    if (!data) {
      return []
    }
    if (idProperty) {
      return data.Items.map((item) => ({ id: Number(item[idProperty]), ...item }))
    } else {
      return data.Items.map((item, index) => ({ id: index, ...item }))
    }
  }, [data, idProperty])

  const columns: GridColDef[] = useMemo(() => {
    const dateColumn: GridColTypeDef = {
      type: 'dateTime',
      width: 190,
      valueFormatter: ({ value }) => intl.formatDate(
        new Date(String(value)), {
          dateStyle: 'short',
          timeStyle: 'medium'
        }
      )
    }

    const filtered = schema.Properties.filter(property =>
      !ignoredProperties?.includes(property.PropertyName)
    )
    return filtered.map(property => {
      const custom = find(customColumns, ['property', property.PropertyName])
      if (custom) {
        return {
          field: property.PropertyName,
          headerName: camelCaseToTitle(property.PropertyName),
          ...custom.gridColTypeDef
        }
      }
      if (property.JsPropertyType === 'Date') {
        return {
          field: property.PropertyName,
          headerName: camelCaseToTitle(property.PropertyName),
          ...dateColumn
        }
      } else {
        return {
          field: property.PropertyName,
          headerName: camelCaseToTitle(property.PropertyName),
          type: property.JsPropertyType,
          width: widths && widths[property.PropertyName]
            ? widths[property.PropertyName]
            : 100
        }
      }
    })
  }, [customColumns, ignoredProperties, intl, schema, widths])

  const handleFilterModelChange = (params: GridFilterModelParams) => {
    if (!onFilterChange) {
      return
    }
    const expressions: Expression[] = []
    for (const item of params.filterModel.items) {
      if (item.columnField && item.operatorValue && item.value) {
        expressions.push({
          Prop: item.columnField,
          Op: convertOperator(item.operatorValue),
          Val: item.value
        })
      }
    }
    onFilterChange(expressions)
  }

  const handlePageChange = (params: GridPageChangeParams) => {
    if (!isNil(onPageChange) && params.page !== page) {
      onPageChange(params.page)
    }
    if (!isNil(onRowsPerPageChange) && params.pageSize !== rowsPerPage) {
      onRowsPerPageChange(params.pageSize)
    }
  }

  const handleSelectionModelChange = (params: GridSelectionModelChangeParams) => {
    if (!onSelectionChange || !data) {
      return
    }
    if (idProperty) {
      onSelectionChange(
        params.selectionModel.map(i => Number(data.Items[Number(i)][idProperty]))
      )
    } else {
      onSelectionChange(params.selectionModel.map(i => Number(i)))
    }
  }

  const handleSortModelChange = (params: GridSortModelParams) => {
    if (!onSortChange) {
      return
    }
    onSortChange(params.sortModel)
  }

  return (
    <DataGrid
      checkboxSelection={checkboxSelection}
      columns={columns}
      filterMode={filterMode}
      loading={loading}
      onFilterModelChange={handleFilterModelChange}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageChange}
      onSelectionModelChange={handleSelectionModelChange}
      onSortModelChange={handleSortModelChange}
      page={page}
      pageSize={rowsPerPage}
      paginationMode={paginationMode}
      rowCount={totalRows}
      rows={rows}
      rowsPerPageOptions={rowsPerPageOptions}
      sortModel={sort}
      sortingMode={sortingMode}
    />
  )
}

export default EnhancedDataGrid
